// src/hooks/useSupabaseAuth.js - Supabase Authentication Hook
import { useState, useEffect, createContext, useContext, useCallback } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import logger from '../services/logger'

const SupabaseAuthContext = createContext({})

export const SupabaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Initialize auth state
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      logger.warn('Supabase not configured - auth unavailable')
      setLoading(false)
      return
    }

    let mounted = true

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession()

        if (error) throw error

        if (mounted) {
          setSession(currentSession)
          setUser(currentSession?.user ?? null)
          logger.info('✅ Supabase session initialized:', currentSession?.user?.email)
        }
      } catch (err) {
        logger.error('❌ Error initializing auth:', err)
        if (mounted) {
          setError(err.message)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        logger.debug('🔐 Auth state changed:', event, currentSession?.user?.email)

        if (mounted) {
          setSession(currentSession)
          setUser(currentSession?.user ?? null)
          setError(null)
        }

        // Handle specific auth events
        if (event === 'SIGNED_IN') {
          logger.info('✅ User signed in:', currentSession?.user?.email)
        } else if (event === 'SIGNED_OUT') {
          logger.info('👋 User signed out')
        } else if (event === 'TOKEN_REFRESHED') {
          logger.debug('🔄 Token refreshed')
        } else if (event === 'USER_UPDATED') {
          logger.debug('👤 User updated')
        }
      }
    )

    return () => {
      mounted = false
      subscription?.unsubscribe()
    }
  }, [])

  // Sign up with email/password
  const signUp = useCallback(async (email, password, metadata = {}) => {
    try {
      setError(null)
      setLoading(true)

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata // Additional user metadata
        }
      })

      if (error) throw error

      logger.info('✅ Sign up successful:', data.user?.email)
      return { data, error: null }
    } catch (err) {
      logger.error('❌ Sign up error:', err)
      setError(err.message)
      return { data: null, error: err }
    } finally {
      setLoading(false)
    }
  }, [])

  // Sign in with email/password
  const signIn = useCallback(async (email, password) => {
    try {
      setError(null)
      setLoading(true)

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      logger.info('✅ Sign in successful:', data.user?.email)
      return { data, error: null }
    } catch (err) {
      logger.error('❌ Sign in error:', err)
      setError(err.message)
      return { data: null, error: err }
    } finally {
      setLoading(false)
    }
  }, [])

  // Sign in with OAuth provider (Spotify, Google/YouTube)
  const signInWithProvider = useCallback(async (provider, options = {}) => {
    try {
      setError(null)

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider, // 'spotify' or 'google'
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: options.scopes,
          ...options
        }
      })

      if (error) throw error

      logger.info(`✅ ${provider} OAuth initiated`)
      return { data, error: null }
    } catch (err) {
      logger.error(`❌ ${provider} OAuth error:`, err)
      setError(err.message)
      return { data: null, error: err }
    }
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    try {
      setError(null)
      const { error } = await supabase.auth.signOut()

      if (error) throw error

      logger.info('✅ Sign out successful')
      return { error: null }
    } catch (err) {
      logger.error('❌ Sign out error:', err)
      setError(err.message)
      return { error: err }
    }
  }, [])

  // Get user profile from database
  const getUserProfile = useCallback(async () => {
    if (!user) return null

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error

      return data
    } catch (err) {
      logger.error('❌ Error fetching user profile:', err)
      return null
    }
  }, [user])

  // Update user profile
  const updateUserProfile = useCallback(async (updates) => {
    if (!user) return { error: new Error('No user logged in') }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error

      logger.info('✅ Profile updated successfully')
      return { data, error: null }
    } catch (err) {
      logger.error('❌ Error updating profile:', err)
      return { data: null, error: err }
    }
  }, [user])

  // Reset password
  const resetPassword = useCallback(async (email) => {
    try {
      setError(null)
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      })

      if (error) throw error

      logger.info('✅ Password reset email sent')
      return { data, error: null }
    } catch (err) {
      logger.error('❌ Password reset error:', err)
      setError(err.message)
      return { data: null, error: err }
    }
  }, [])

  // Update password
  const updatePassword = useCallback(async (newPassword) => {
    try {
      setError(null)
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      logger.info('✅ Password updated successfully')
      return { data, error: null }
    } catch (err) {
      logger.error('❌ Password update error:', err)
      setError(err.message)
      return { data: null, error: err }
    }
  }, [])

  const value = {
    user,
    session,
    loading,
    error,
    isAuthenticated: !!user,
    signUp,
    signIn,
    signInWithProvider,
    signOut,
    getUserProfile,
    updateUserProfile,
    resetPassword,
    updatePassword
  }

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  )
}

// Hook to use Supabase auth context
export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext)
  if (!context) {
    throw new Error('useSupabaseAuth must be used within SupabaseAuthProvider')
  }
  return context
}

// Helper function to check if using Supabase auth
export const isUsingSupabaseAuth = () => {
  return isSupabaseConfigured() && !!localStorage.getItem('supabase.auth.token')
}
